import { Category, MenuType } from "@hermes/api-model-menu";

import { FlatCategories } from "../model/menu.model";

import { cleanMenuEntryName } from "./entry.helper";

export interface CategoryWithParent extends Category {
    parent?: string;
    parentName?: string;
}

/**
 * Flatten data from menu entries (in tree) into a key-value object
 * - key : PIM Code
 * - value : CategoryEntry
 *
 * TRIVIA :
 * Since menu entries are in a tree structure, this function goes
 * through entries recursively (using depth first search)
 *
 * @param menuEntries menu entries in a tree structure
 * @param categories Already flatten categories, starts with empty object
 * @param parentPimCode Parent PIM code, starts with undefined
 * @param parentName Parent name, starts with undefined
 * @returns Flattened categories
 */
export const flattenMenuToCategories = (
    menuEntries: Category[] | undefined,
    categories: Record<string, CategoryWithParent> = {},
    parentPimCode: string | undefined = undefined,
    parentName: string | undefined = undefined,
): Record<string, CategoryWithParent> => {
    if (menuEntries) {
        menuEntries.forEach((menuEntry) => {
            flattenMenuToCategories(
                menuEntry.subcategories,
                categories,
                menuEntry.pimCode,
                menuEntry.name,
            );

            if (isCategory(menuEntry) && menuEntry.pimCode) {
                categories[menuEntry.pimCode] = {
                    ...menuEntry,
                    name: cleanMenuEntryName(menuEntry.name, false),
                    subcategories: [], // Flat data, cleanup existing children
                    parent: parentPimCode,
                    parentName,
                };
            }
        });
    }
    return categories;
};

export const isCategory = (menuEntry: Category): menuEntry is Category =>
    menuEntry.type === MenuType.TYPE_GRID ||
    menuEntry.type === MenuType.TYPE_PUSH ||
    menuEntry.type === MenuType.TYPE_OTHER ||
    menuEntry.type === MenuType.TYPE_STORY;

/**
 * Get path to product grid from a category entry
 */
export const getGridPath = (category: Category): string =>
    category.categoryPath;

/**
 * Return a category from flat categories given a PIM code
 *
 * @param categories Flat categories
 * @param pimCode PIM code
 * @returns Category
 * @deprecated Should be removed when PIM code standardization is done
 */
export const getCategory = (
    categories: FlatCategories,
    pimCode: string,
): Category => {
    // The format index could be:
    // - WOMENSHOES (D7)
    // - WOMEN_SHOES (D10)
    // So loop on flat categories and remove underscore from index to keep it without underscore and in uppercase
    const newCategories = removeUnderscoresFromKeys(categories);

    // We format pimCode to be compliant with the index format in newCategories (Uppercase and without underscore)
    const formattedPimCode = pimCode.toUpperCase().replace(/_/g, "");
    return newCategories[formattedPimCode];
};

/**
 * Remove the underscore from index of FlatCategories
 *
 * @param FlatCategories Flat categories
 * @returns FlatCategories : Flat categories with index without underscore
 */
export const removeUnderscoresFromKeys = (
    flatCategories: FlatCategories,
): FlatCategories => {
    const result: FlatCategories = {};

    Object.entries(flatCategories).forEach(([key, value]) => {
        const newKey = key.includes("_")
            ? key.replace(/_/g, "").toUpperCase()
            : key.toUpperCase();
        result[newKey] = value;
    });

    return result;
};
